import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

export const CalendarWrapper = styled(Box)(({ theme }) => ({
  // Responsive sizing: fill the available width and height provided by the parent flex column.
  width: '100%',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',

  // Let the calendar grow to fill the wrapper
  '& .rbc-calendar': {
    flex: 1,
    minHeight: 0,
    // Apply the MUI theme font to the entire calendar
    fontFamily: theme.typography.fontFamily,
  },

  // Form controls (buttons / links / inputs) don't inherit font-family by default,
  // so force them to inherit the MUI font set on .rbc-calendar
  '& .rbc-calendar button, & .rbc-calendar a, & .rbc-calendar input, & .rbc-calendar select, & .rbc-calendar textarea':
    {
      fontFamily: 'inherit',
    },

  // Weekday / column headers
  '& .rbc-header': {
    padding: theme.spacing(1),
    ...theme.typography.subtitle2,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  },

  // Grid borders
  '& .rbc-month-view, & .rbc-time-view, & .rbc-time-header, & .rbc-time-header-content, & .rbc-day-bg, & .rbc-month-row, & .rbc-time-content, & .rbc-header + .rbc-header, & .rbc-day-slot .rbc-time-slot':
    {
      borderColor: theme.palette.divider,
    },

  // Today's cell
  '& .rbc-today': {
    backgroundColor: theme.palette.action.hover,
  },

  // Days outside the current month
  '& .rbc-off-range-bg': {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  '& .rbc-off-range': {
    color: theme.palette.text.disabled,
  },

  // Date numbers
  '& .rbc-date-cell': {
    padding: theme.spacing(0.5),
    ...theme.typography.body2,
    color: theme.palette.text.primary,
  },

  // Events — reset the library defaults so the custom event card owns its look
  '& .rbc-event': {
    padding: 0,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    ...theme.typography.caption,
  },
  // Taller time-grid rows so short (e.g. 15-min) events get enough pixels to render
  // one readable line and stay proportional to longer events (default is ~40px/hour)
  '& .rbc-timeslot-group': {
    minHeight: '5rem',
  },
  // In the time grid, let the event body fill its slot and clip instead of growing
  '& .rbc-time-view .rbc-event': {
    minHeight: 0,
  },
  // Native time label is hidden: the custom event card renders the time itself
  '& .rbc-event-label': {
    display: 'none',
  },
  '& .rbc-event-content': {
    padding: 0,
  },
  '& .rbc-event.rbc-selected': {
    backgroundColor: theme.palette.action.selected,
  },
  '& .rbc-event:focus': {
    outline: `2px solid ${theme.palette.primary.light}`,
  },
  '& .rbc-show-more': {
    ...theme.typography.caption,
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },

  // Toolbar (navigation + view switcher)
  '& .rbc-toolbar': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    padding: 0,
  },
  // Calendar date (e.g. "October 2026") moved to the left
  '& .rbc-toolbar .rbc-toolbar-label': {
    order: -1,
    flex: '0 0 auto',
    textAlign: 'left',
    ...theme.typography.h6,
    color: theme.palette.text.primary,
  },
  // Navigation buttons (Today / Back / Next) moved to the right
  '& .rbc-toolbar .rbc-btn-group': {
    order: 1,
    marginLeft: 'auto',
  },
  // Hide the empty view-switcher group that RBC always renders
  '& .rbc-toolbar .rbc-btn-group:empty': {
    display: 'none',
    margin: 0,
  },
  '& .rbc-toolbar button': {
    textTransform: 'none',
    padding: theme.spacing(0.25, 1),
    color: theme.palette.common.white,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create(['background-color', 'color', 'border-color']),
  },
  '& .rbc-toolbar button:hover': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '& .rbc-toolbar button.rbc-active, & .rbc-toolbar button.rbc-active:hover, & .rbc-toolbar button.rbc-active:focus': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));
